import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import Modal from '@/components/ui/Modal';
import { Loader2, ArrowDown, ArrowUp, History as HistoryIcon, Banknote, CreditCard, ArrowRightLeft, QrCode, Tag } from 'lucide-react';
import { formatPrice, formatDateTime } from '@/utils/format';

interface PriceChangeLog {
  id: number;
  field: 'cost_price' | 'sale_price' | 'list_price' | 'transfer_price' | 'qr_price';
  old_value: number | null;
  new_value: number | null;
  change_type: string;
  bulk_change_id: string | null;
  reason: string | null;
  user_name: string | null;
  created_at: string;
}

const FIELD_LABELS: Record<PriceChangeLog['field'], { label: string; icon: React.ReactNode; color: string }> = {
  cost_price: { label: 'Costo', icon: <Tag className="h-3.5 w-3.5" />, color: 'bg-gray-100 text-gray-700' },
  sale_price: { label: 'Efectivo', icon: <Banknote className="h-3.5 w-3.5" />, color: 'bg-emerald-100 text-emerald-700' },
  list_price: { label: 'Tarjeta', icon: <CreditCard className="h-3.5 w-3.5" />, color: 'bg-blue-100 text-blue-700' },
  transfer_price: { label: 'Transferencia', icon: <ArrowRightLeft className="h-3.5 w-3.5" />, color: 'bg-violet-100 text-violet-700' },
  qr_price: { label: 'QR', icon: <QrCode className="h-3.5 w-3.5" />, color: 'bg-orange-100 text-orange-700' },
};

const CHANGE_TYPE_LABELS: Record<string, string> = {
  manual: 'Edición manual',
  auto_from_cost: 'Recálculo por costo',
  bulk: 'Cambio masivo',
  purchase_order: 'Orden de compra',
  category_recalc: 'Recálculo de categoría',
};

interface PriceHistoryModalProps {
  productId: number;
  productName: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function PriceHistoryModal({ productId, productName, isOpen, onClose }: PriceHistoryModalProps) {
  const { data: logs, isLoading } = useQuery<PriceChangeLog[]>({
    queryKey: ['price-history', productId],
    queryFn: async () => {
      const res = await api.get(`/products/${productId}/price-history`);
      return res.data;
    },
    enabled: isOpen,
    staleTime: 0,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Historial de precios — ${productName}`} size="xl">
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-taupe-600" />
        </div>
      ) : !logs || logs.length === 0 ? (
        <div className="text-center py-12">
          <HistoryIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Sin cambios registrados todavía</p>
          <p className="text-xs text-gray-400 mt-1">Cuando edites costos o precios aparecerán acá</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {logs.map((log) => {
            const meta = FIELD_LABELS[log.field];
            const oldV = log.old_value;
            const newV = log.new_value;
            const isUp = oldV !== null && newV !== null && newV > oldV;
            const isDown = oldV !== null && newV !== null && newV < oldV;
            const diff = oldV !== null && newV !== null && oldV > 0
              ? ((newV - oldV) / oldV) * 100
              : null;

            return (
              <div key={log.id} className="bg-white border border-gray-200 rounded-xl p-3 hover:border-gray-300 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${meta.color}`}>
                      {meta.icon}
                      {meta.label}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      {oldV !== null ? (
                        <span className="text-gray-500 line-through">{formatPrice(oldV)}</span>
                      ) : (
                        <span className="text-gray-400 italic">sin valor</span>
                      )}
                      {isUp && <ArrowUp className="h-3.5 w-3.5 text-red-500" />}
                      {isDown && <ArrowDown className="h-3.5 w-3.5 text-emerald-600" />}
                      {!isUp && !isDown && <span className="text-gray-400">→</span>}
                      <span className={`font-bold ${isUp ? 'text-red-700' : isDown ? 'text-emerald-700' : 'text-gray-900'}`}>
                        {newV !== null ? formatPrice(newV) : 'borrado'}
                      </span>
                      {diff !== null && (
                        <span className={`text-xs ${isUp ? 'text-red-600' : 'text-emerald-600'}`}>
                          ({diff > 0 ? '+' : ''}{diff.toFixed(1)}%)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                      <span>{CHANGE_TYPE_LABELS[log.change_type] || log.change_type}</span>
                      {log.user_name && <><span>·</span><span>{log.user_name}</span></>}
                      <span>·</span>
                      <span>{formatDateTime(log.created_at)}</span>
                    </div>
                    {log.reason && (
                      <p className="text-xs text-gray-600 mt-1 italic">{log.reason}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
